import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

import { navItems, pages, type NavGroup } from "@/app/registry";
import { UNGATED, usePlanInactive } from "./planAccess";
import { useSession } from "@/app/session";
import { cn } from "@/lib/cn";

import { useSectionLabels } from "./sections";

export function Nav({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useSession();
  const labels = useSectionLabels();
  const inactive = usePlanInactive();
  const all = navItems(
    pages,
    user?.role,
    user?.ai_calls_access === true,
    user?.access_tier ?? "full",
    user?.ra_client === true,
  );
  const items = inactive ? all.filter((p) => UNGATED.includes(p.path)) : all;
  const groups = new Map<NavGroup, typeof items>();
  for (const item of items)
    groups.set(item.nav.group, [...(groups.get(item.nav.group) ?? []), item]);

  return (
    <nav aria-label="Main" className="grid gap-5">
      {[...groups.entries()].map(([group, entries]) => (
        <div key={group}>
          <div className="mb-1.5 flex items-center gap-1.5 px-3 text-2xs font-semibold tracking-wider text-subtle uppercase">
            {entries.some((e) => e.nav.highlight) ? (
              <Sparkles className="size-3 text-[#6d4fd8] dark:text-[#b39dff]" aria-hidden />
            ) : null}
            {labels[group]}
          </div>
          <ul className="grid gap-0.5">
            {entries.map((p) => {
              const Icon = p.nav.icon;
              const Count = p.nav.badge;
              return (
                <li key={p.path}>
                  <Link
                    to={p.path}
                    onClick={onNavigate}
                    className={cn(
                      "flex h-9 items-center gap-2.5 rounded-lg px-3 text-sm text-muted transition-colors",
                      "hover:bg-surface-2 hover:text-fg",
                    )}
                    activeProps={{
                      className: "bg-brand-soft !text-brand font-medium",
                      "aria-current": "page",
                    }}
                  >
                    <Icon className={cn("size-4 shrink-0", p.nav.highlight && "text-[#6d4fd8] dark:text-[#b39dff]")} />
                    <span className="truncate">{p.nav.label}</span>
                    {Count ? <Count /> : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
